import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiTrash2, FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Loading from '../../ions/Loading';
import SelectTextField from '../../molecules/SelectTextField';
import { contractApi } from '../../../services/contractApi';
import { personApi } from '../../../services/personApi';
import { PERSON_PRODUCTS } from '../../../constants/personProducts';
import type { RoleRequirementDto, ContractStructureDto } from '../../../services/contractApi';
import AuditHistoryPanel from './AuditHistoryPanel';

const SIGNER_ROLES = [
    { value: 'EMITENTE', label: 'Emitente' },
    { value: 'TESTEMUNHA', label: 'Testemunha' },
    { value: 'VALIDADOR', label: 'Validador' },
    { value: 'AVALISTA', label: 'Avalista' },
    { value: 'AVALISTA_PF', label: 'Avalista PF' },
    { value: 'AVALISTA_PJ', label: 'Avalista PJ' },
    { value: 'CEDENTE', label: 'Cedente' },
    { value: 'CESSIONARIA', label: 'Cessionária' },
    { value: 'CREDORA', label: 'Credora' },
    { value: 'CREDORA_FIDUCIARIA', label: 'Credora Fiduciária' },
    { value: 'DEVEDOR_FIDUCIARIO', label: 'Devedor Fiduciário' },
    { value: 'INTERVENIENTE_ANUENTE', label: 'Interveniente Anuente' },
];

const PARTICIPANT_SOURCES = [
    { value: 'RECIPIENT', label: 'Cessionária (RECIPIENT)' },
    { value: 'ASSIGNOR', label: 'Cedente (ASSIGNOR)' },
    { value: 'ANY', label: 'Qualquer (ANY)' },
];

interface FormRow {
    id: string;
    role: string;
    label: string;
    minCount: number;
    participantSource: RoleRequirementDto['participantSource'];
    signingOrder: number;
}

const generateId = (): string =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

// --- DefaultObserversSection: search Person by name, email or CPF ---
interface DefaultObserversSectionProps {
    name: string;
    defaultObservers: { personUuid?: string; name: string; email: string }[];
    setDefaultObservers: React.Dispatch<React.SetStateAction<{ personUuid?: string; name: string; email: string }[]>>;
    observerSaving: boolean;
    setObserverSaving: React.Dispatch<React.SetStateAction<boolean>>;
}

function DefaultObserversSection({ name, defaultObservers, setDefaultObservers, observerSaving, setObserverSaving }: DefaultObserversSectionProps) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<{ uuid: string; name: string; email: string }[]>([]);
    const [searching, setSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Cleanup on unmount: cancel pending timeout and abort in-flight request
    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (abortRef.current) abortRef.current.abort();
        };
    }, []);

    const handleSearch = (value: string) => {
        setQuery(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (value.trim().length < 3) { setResults([]); setShowDropdown(false); return; }

        debounceRef.current = setTimeout(async () => {
            if (abortRef.current) abortRef.current.abort();
            abortRef.current = new AbortController();
            setSearching(true);
            try {
                const people = await personApi.searchPeople(value.trim(), abortRef.current.signal);
                const mapped = people
                    .filter((p: any) => p.email)
                    .map((p: any) => ({ uuid: p.uuid, name: p.name, email: p.email }));
                setResults(mapped);
                setShowDropdown(mapped.length > 0);
            } catch (e: any) {
                if (e.name !== 'AbortError') setResults([]);
            } finally {
                setSearching(false);
            }
        }, 300);
    };

    const handleSelect = (person: { uuid: string; name: string; email: string }) => {
        const email = person.email.toLowerCase();
        if (defaultObservers.some(o => o.email.toLowerCase() === email)) {
            toast.warn('Observador já adicionado');
            return;
        }
        setDefaultObservers(prev => [...prev, { personUuid: person.uuid, name: person.name, email }]);
        setQuery('');
        setResults([]);
        setShowDropdown(false);
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Observadores Padrão</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Pessoas que sempre receberão cópia dos envelopes de assinatura deste tipo de contrato.</p>

            {/* Observer list */}
            {defaultObservers.length > 0 && (
                <div className="space-y-2 mb-4">
                    {defaultObservers.map((obs, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-100 dark:border-slate-700">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{obs.name}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">{obs.email}</span>
                            <button
                                onClick={() => setDefaultObservers(prev => prev.filter((_, idx) => idx !== i))}
                                className="ml-auto p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                title="Remover"
                            >
                                <FiTrash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Search with dropdown */}
            <div className="relative">
                <input
                    type="text"
                    value={query}
                    onChange={e => handleSearch(e.target.value)}
                    onFocus={() => results.length > 0 && setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    placeholder="Buscar por nome, email ou CPF..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 dark:text-white"
                />
                {searching && (
                    <span className="absolute right-3 top-2.5 text-xs text-gray-400">Buscando...</span>
                )}
                {showDropdown && results.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {results.map((p, i) => (
                            <button
                                key={i}
                                type="button"
                                onMouseDown={() => handleSelect(p)}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-sm flex items-center gap-2"
                            >
                                <span className="font-medium text-gray-900 dark:text-white">{p.name}</span>
                                <span className="text-gray-400 dark:text-gray-500">{p.email}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Save observers */}
            <div className="flex justify-end mt-4 pt-3 border-t border-gray-200 dark:border-slate-700">
                <button
                    onClick={async () => {
                        setObserverSaving(true);
                        try {
                            await contractApi.updateContractTypeDefaultObservers(name, defaultObservers);
                            toast.success('Observadores padrão salvos');
                        } catch (e: any) {
                            toast.error(e.message || 'Erro ao salvar observadores');
                        } finally {
                            setObserverSaving(false);
                        }
                    }}
                    disabled={observerSaving}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                    <FiSave className="w-4 h-4" />
                    {observerSaving ? 'Salvando...' : 'Salvar Observadores'}
                </button>
            </div>
        </div>
    );
}

// --- AllowedProductsSection: multi-select of products for signatory filtering ---
interface AllowedProductsSectionProps {
    name: string;
    allowedProducts: string[];
    setAllowedProducts: React.Dispatch<React.SetStateAction<string[]>>;
}

function AllowedProductsSection({ name, allowedProducts, setAllowedProducts }: AllowedProductsSectionProps) {
    const [saving, setSaving] = useState(false);

    const toggleProduct = (product: string) => {
        setAllowedProducts(prev =>
            prev.includes(product)
                ? prev.filter(p => p !== product)
                : [...prev, product]
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await contractApi.updateContractTypeAllowedProducts(name, allowedProducts);
            toast.success('Produtos permitidos salvos');
        } catch (e: any) {
            toast.error(e.message || 'Erro ao salvar produtos');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Produtos Permitidos</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Signatários serão filtrados por estes produtos. Se nenhum for selecionado, todos os signatários ativos aparecem (retrocompatível).
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                {(PERSON_PRODUCTS as readonly string[]).map((product: string) => {
                    const isSelected = allowedProducts.includes(product);
                    return (
                        <label
                            key={product}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                                isSelected
                                    ? 'border-blue-400 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20'
                                    : 'border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-800 hover:border-gray-300'
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleProduct(product)}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{product}</span>
                        </label>
                    );
                })}
            </div>

            {allowedProducts.length > 0 && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
                    {allowedProducts.length} produto(s) selecionado(s): signatários sem vínculo com estes produtos serão filtrados.
                </p>
            )}
            {allowedProducts.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
                    Nenhum produto selecionado: todos os signatários ativos aparecerão (sem filtro por produto).
                </p>
            )}

            <div className="flex justify-end pt-3 border-t border-gray-200 dark:border-slate-700">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                    <FiSave className="w-4 h-4" />
                    {saving ? 'Salvando...' : 'Salvar Produtos'}
                </button>
            </div>
        </div>
    );
}

const createEmptyRow = (): FormRow => ({
    id: generateId(),
    role: '',
    label: '',
    minCount: 1,
    participantSource: 'ANY',
    signingOrder: 1,
});

const ContractTypeStructureForm = () => {
    const { name } = useParams<{ name: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(name !== 'new');
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState<FormRow[]>([createEmptyRow()]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [newTypeName, setNewTypeName] = useState('');
    const [defaultObservers, setDefaultObservers] = useState<{ personUuid?: string; name: string; email: string }[]>([]);
    const [allowedProducts, setAllowedProducts] = useState<string[]>([]);
    const [observerSaving, setObserverSaving] = useState(false);
    const isCreateMode = name === 'new';

    useEffect(() => {
        if (name && !isCreateMode) loadContractType();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name]);

    const loadContractType = async () => {
        setLoading(true);
        try {
            const ct = await contractApi.getContractType(name!);
            if (ct.structures && ct.structures.length > 0) {
                const roles = ct.structures[0].roles || [];
                setRows(roles.map(r => ({
                    id: generateId(),
                    role: r.role,
                    label: r.label,
                    minCount: r.minCount,
                    participantSource: r.participantSource,
                    signingOrder: r.signingOrder,
                })));
            } else {
                setRows([createEmptyRow()]);
            }
            setDefaultObservers(ct.defaultObservers || []);
            setAllowedProducts(ct.allowedProducts || []);
        } catch (e: any) {
            toast.error(e.message || 'Erro ao carregar tipo de contrato');
        } finally {
            setLoading(false);
        }
    };

    const handleAddRow = () => {
        setRows(prev => [...prev, createEmptyRow()]);
    };

    const handleRemoveRow = (id: string) => {
        setRows(prev => prev.filter(r => r.id !== id));
        setErrors(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(key => {
                if (key.startsWith(id)) delete next[key];
            });
            return next;
        });
    };

    const handleFieldChange = (id: string, field: keyof FormRow, value: string | number) => {
        setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
        // Clear error on change
        setErrors(prev => {
            const next = { ...prev };
            delete next[`${id}.${field}`];
            return next;
        });
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (rows.length === 0) {
            toast.error('Pelo menos um papel é obrigatório');
            return false;
        }
        const seenRoles = new Set<string>();
        rows.forEach((row) => {
            if (!row.role) newErrors[`${row.id}.role`] = 'Obrigatório';
            if (!row.label) newErrors[`${row.id}.label`] = 'Obrigatório';
            if (row.minCount < 1) newErrors[`${row.id}.minCount`] = 'Mínimo 1';
            if (row.signingOrder < 1) newErrors[`${row.id}.signingOrder`] = 'Mínimo 1';
            if (row.role && seenRoles.has(row.role)) {
                newErrors[`${row.id}.role`] = 'Papel duplicado';
            }
            if (row.role) seenRoles.add(row.role);
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        if (isCreateMode && !newTypeName.trim()) {
            toast.error('Nome do tipo de contrato é obrigatório');
            return;
        }
        setSaving(true);
        try {
            const structures: ContractStructureDto[] = [{
                name: 'primary',
                roles: rows.map(r => ({
                    role: r.role,
                    label: r.label,
                    minCount: r.minCount,
                    required: true,
                    participantSource: r.participantSource,
                    signingOrder: r.signingOrder,
                })),
            }];
            const typeName = isCreateMode ? newTypeName.trim().toUpperCase().replace(/\s+/g, '_') : name!;
            if (isCreateMode) {
                await contractApi.createContractType(typeName, structures);
                toast.success('Tipo de contrato criado com sucesso!');
                navigate(`/contract-types/${typeName}`);
            } else {
                await contractApi.updateContractTypeStructures(typeName, structures);
                toast.success('Estrutura salva com sucesso!');
            }
        } catch (e: any) {
            if (e.message) {
                toast.error(e.message);
            } else {
                toast.error('Erro ao salvar estrutura');
            }
        } finally {
            setSaving(false);
        }
    };

    // Group rows by signingOrder for visual display
    const groupedRows = useMemo(() => {
        const groups: Record<number, FormRow[]> = {};
        rows.forEach(row => {
            const order = row.signingOrder || 1;
            if (!groups[order]) groups[order] = [];
            groups[order].push(row);
        });
        return Object.entries(groups)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([order, items]) => ({ order: Number(order), items }));
    }, [rows]);

    const getError = (rowId: string, field: string) => errors[`${rowId}.${field}`];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800">
            {loading && <Loading />}

            {/* Header */}
            <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4">
                <div className="flex items-center gap-3 max-w-6xl mx-auto">
                    <button onClick={() => navigate('/contract-types')} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                        <FiArrowLeft className="text-gray-600 dark:text-gray-400" />
                    </button>
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {isCreateMode ? 'Novo Tipo de Contrato' : `Estrutura: ${name}`}
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Configuração dos papéis de assinantes</p>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
                {/* Name field (create mode only) */}
                {isCreateMode && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Identificação</h2>
                        <div className="max-w-md">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Nome do Tipo (ex: CESSAO_CREDITO)</label>
                            <input
                                type="text"
                                value={newTypeName}
                                onChange={e => setNewTypeName(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 dark:text-white"
                                placeholder="Ex: CESSAO_CREDITO"
                            />
                        </div>
                    </div>
                )}

                {/* Form */}
                {!loading && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Papéis de Assinante</h2>
                            <button
                                onClick={handleAddRow}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                <FiPlus className="w-4 h-4" />
                                Adicionar Papel
                            </button>
                        </div>

                        {/* Grouped display */}
                        {groupedRows.map(group => (
                            <div key={group.order} className="mb-6 last:mb-0">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                                        Ordem {group.order}
                                    </span>
                                    <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700" />
                                </div>

                                <div className="space-y-3">
                                    {group.items.map(row => (
                                        <div key={row.id} className="space-y-0">
                                        <div className="grid grid-cols-12 gap-3 items-start p-3 rounded-lg border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
                                            {/* Role */}
                                            <div className="col-span-3">
                                                <SelectTextField
                                                    id={`role-${row.id}`}
                                                    name={`role-${row.id}`}
                                                    label="Papel"
                                                    value={row.role}
                                                    onChange={e => handleFieldChange(row.id, 'role', e.target.value)}
                                                    items={SIGNER_ROLES}
                                                    emptyLabel="Selecione..."
                                                    errorMessage={getError(row.id, 'role')}
                                                />
                                            </div>

                                            {/* Label */}
                                            <div className="col-span-3">
                                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Rótulo</label>
                                                <input
                                                    type="text"
                                                    value={row.label}
                                                    onChange={e => handleFieldChange(row.id, 'label', e.target.value)}
                                                    className={`w-full px-2 py-1.5 text-sm border rounded-md bg-white dark:bg-slate-700 dark:text-white ${getError(row.id, 'label') ? 'border-red-400' : 'border-gray-300 dark:border-slate-600'}`}
                                                    placeholder="Ex: Emitente"
                                                />
                                                {getError(row.id, 'label') && <span className="text-xs text-red-500">{getError(row.id, 'label')}</span>}
                                            </div>

                                            {/* Min Count */}
                                            <div className="col-span-1">
                                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Mín.</label>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    value={row.minCount}
                                                    onChange={e => handleFieldChange(row.id, 'minCount', Math.max(1, parseInt(e.target.value) || 1))}
                                                    className={`w-full px-2 py-1.5 text-sm border rounded-md bg-white dark:bg-slate-700 dark:text-white ${getError(row.id, 'minCount') ? 'border-red-400' : 'border-gray-300 dark:border-slate-600'}`}
                                                />
                                                {getError(row.id, 'minCount') && <span className="text-xs text-red-500">{getError(row.id, 'minCount')}</span>}
                                            </div>

                                            {/* Participant Source */}
                                            <div className="col-span-3">
                                                <SelectTextField
                                                    id={`source-${row.id}`}
                                                    name={`source-${row.id}`}
                                                    label="Fonte"
                                                    value={row.participantSource}
                                                    onChange={e => handleFieldChange(row.id, 'participantSource', e.target.value)}
                                                    items={PARTICIPANT_SOURCES}
                                                    emptyLabel="Selecione..."
                                                />
                                            </div>

                                            {/* Signing Order */}
                                            <div className="col-span-1">
                                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Ordem</label>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    value={row.signingOrder}
                                                    onChange={e => handleFieldChange(row.id, 'signingOrder', Math.max(1, parseInt(e.target.value) || 1))}
                                                    className={`w-full px-2 py-1.5 text-sm border rounded-md bg-white dark:bg-slate-700 dark:text-white ${getError(row.id, 'signingOrder') ? 'border-red-400' : 'border-gray-300 dark:border-slate-600'}`}
                                                />
                                                {getError(row.id, 'signingOrder') && <span className="text-xs text-red-500">{getError(row.id, 'signingOrder')}</span>}
                                            </div>

                                            {/* Remove */}
                                            <div className="col-span-1 flex items-end pb-1">
                                                <button
                                                    onClick={() => handleRemoveRow(row.id)}
                                                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                                                    title="Remover"
                                                >
                                                    <FiTrash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {rows.length === 0 && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                                Nenhum papel configurado. Clique em "Adicionar Papel" para começar.
                            </p>
                        )}

                        {/* Save button */}
                        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 dark:border-slate-700">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                            >
                                <FiSave className="w-4 h-4" />
                                {saving ? 'Salvando...' : 'Salvar Estrutura'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Audit History */}
                {!loading && name && !isCreateMode && (
                    <>
                        {/* Default Observers */}
                        <DefaultObserversSection
                            name={name!}
                            defaultObservers={defaultObservers}
                            setDefaultObservers={setDefaultObservers}
                            observerSaving={observerSaving}
                            setObserverSaving={setObserverSaving}
                        />

                        {/* Allowed Products */}
                        <AllowedProductsSection
                            name={name!}
                            allowedProducts={allowedProducts}
                            setAllowedProducts={setAllowedProducts}
                        />

                        <AuditHistoryPanel contractTypeName={name} />
                    </>
                )}
            </div>
        </div>
    );
};

export default ContractTypeStructureForm;
